import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import {
    getFormattedValueAndUnitFromAreaValue,
    getFormattedValueAndUnitFromMonetaryValue,
    getFormattedValueAndUnitFromPercentage,
    getFormattedValueAndUnitFromWault,
    getLeadingValuePerSqm,
    getLeadingValuePerSqmLabels,
    InfoTooltip,
    BlockMath,
    MetricGrid,
    tokens,
} from 'ComponentLibrary'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export const KeyValuesGrid = ({ valuation }: { valuation: ExistingValuation }) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tValuationOverview } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.valuation.valuationOverview' })
    const locale = useCurrentResolvedLocale()
    const { valuePerSqmLabel, valuePerSqmValue, areaLabel, areaValue } = getLeadingValuePerSqm(
        valuation,
        getLeadingValuePerSqmLabels(tCommon)
    )
    const hasIncomeApproach = valuation.selected_valuation_methods.includes('income')
    const isValuationHistorical = valuation.valuation_type === 'historical'

    return (
        <Stack direction="column" height="100%" justifyContent="center">
            <MetricGrid
                size="small"
                variant="spacious"
                numColumns={hasIncomeApproach && !isValuationHistorical ? 4 : 2}
                values={[
                    {
                        ...getFormattedValueAndUnitFromMonetaryValue(valuation.market_value_with_trend.value, locale),
                        subheadline: tCommon('marketValue'),
                    },
                    hasIncomeApproach && {
                        ...getFormattedValueAndUnitFromMonetaryValue(valuation.tenant_summary?.total?.gross_rental_income?.total, locale),
                        subheadline: tCommon('marketRent'),
                    },
                    !isValuationHistorical && {
                        ...getFormattedValueAndUnitFromAreaValue(areaValue, locale),
                        subheadline: areaLabel,
                    },
                    hasIncomeApproach &&
                        !isValuationHistorical && {
                            ...getFormattedValueAndUnitFromPercentage(valuation.gross_initial_yield, undefined, locale),
                            subheadline: tCommon('grossInitialYield'),
                            endAdornment: (
                                <InfoTooltip
                                    text={<BlockMath math={tValuationOverview('formulas.grossInitialYield')} />}
                                    size={tokens.SizingIconS}
                                />
                            ),
                        },
                    {
                        ...getFormattedValueAndUnitFromPercentage(valuation.market_value_with_trend.trend, undefined, locale),
                        subheadline: tCommon('valueTrend'),
                    },
                    {
                        ...getFormattedValueAndUnitFromMonetaryValue(valuePerSqmValue, locale),
                        subheadline: valuePerSqmLabel,
                    },
                    hasIncomeApproach &&
                        !isValuationHistorical && {
                            ...getFormattedValueAndUnitFromWault(
                                valuation.wault,
                                {
                                    unknown: tCommon('unknown'),
                                    unlimited: tCommon('unlimited'),
                                    year: tCommon('year'),
                                    years: tCommon('years'),
                                },
                                locale
                            ),
                            color: valuation.wault === undefined ? tokens.ColorTextSubtlest : undefined,
                            subheadline: tCommon('wault'),
                        },
                    hasIncomeApproach &&
                        !isValuationHistorical && {
                            ...getFormattedValueAndUnitFromPercentage(valuation.gross_yield, undefined, locale),
                            subheadline: tCommon('grossYield'),
                            endAdornment: (
                                <InfoTooltip
                                    text={<BlockMath math={tValuationOverview('formulas.grossYield')} />}
                                    size={tokens.SizingIconS}
                                />
                            ),
                        },
                ].filter((item) => item !== false)}
            />
        </Stack>
    )
}
